// FR202 hardware profile, GPIO I/O, and retirement modals for the edge runtimes
// page. Split from platformEdgeRuntimes.components.js; externally-used pieces
// are re-exported by that barrel.
import React, { useCallback, useEffect, useRef, useState } from "react";

import { GatewayModal } from "../../../components/GatewayModal";
import { GatewayStatusPill } from "../../../components/GatewayStatus";
import GatewayTabs from "../../../components/GatewayTabs";
import NudgeNumber from "../../../components/NudgeNumber";
import Select from "../../../components/Select";
import TagPicker from "../../../components/TagPicker";
import {
  confirmEdgeConnector,
  getEdgeRuntimeHardware,
  getEdgeFR202RetirementStatus,
  inspectEdgeTagCatalog,
  listEdgeConnectors,
  listEdgeGatewayCommands,
  previewEdgeConnector,
  readEdgeFR202ChannelSample,
  removeEdgeFR202HardwareProfile,
  retireEdgeFR202Tags,
  runEdgeFR202SelfTest,
} from "../../../api";
import { useNotify } from "../../../ToastContext";
import chevronIcon from "../../../assets/chevron-right.svg";
import { preparePendingConnector } from "./EdgeConnectorReview";
import { buildFR202AnalogConnectorSpec, edgeConnectorTagEligibility } from "./edge-connector-spec";
import { normalizeRuntime, runtimeHardwareView } from "./platformEdgeRuntimes.model";

export const FR202_PROFILE_ID = "onlogic-fr202-v1";
const FR202_PROFILE_REMOVAL_SCHEMA = "controlseat.edge.hardware-profile-removal";
const FR202_TAG_RETIREMENT_CONFIRMATION_SCHEMA = "controlseat.fr202.tag-retirement-confirmation";

// The profile is a fixed constant on both sides — the server compares it to
// HardwareProfileOnLogicFR202V1, never to operator input — so the client
// supplies it and only the runtime name is typed. Making an operator
// transcribe a slug they cannot be expected to know proved nothing and was
// the single biggest source of confusion in this dialog.
export const fr202RetirementConfirmation = ({ gateway, action, gatewayName, profile = FR202_PROFILE_ID, reason }) => ({
  schema: action === "remove-profile" ? FR202_PROFILE_REMOVAL_SCHEMA : FR202_TAG_RETIREMENT_CONFIRMATION_SCHEMA,
  version: 1,
  gateway_id: gateway?.id || "",
  gateway_name: gatewayName,
  profile,
  reason: reason.trim(),
});

export const fr202RetirementConfirmationMatches = ({ gateway, gatewayName, profile = FR202_PROFILE_ID, reason }) => (
  gatewayName === gateway?.name
  && profile === FR202_PROFILE_ID
  && reason.trim().length >= 3
  && reason.length <= 512
);

export function FR202RetirementConfirmModal({ gateway, action, busy, onCancel, onConfirm }) {
  const [gatewayName, setGatewayName] = useState("");
  const [reason, setReason] = useState("");
  const valid = fr202RetirementConfirmationMatches({ gateway, gatewayName, reason });
  const removingProfile = action === "remove-profile";
  return (
    <GatewayModal
      title={removingProfile ? "Remove FR202 profile" : "Retire FR202 terminal tags"}
      subtitle={removingProfile
        ? "This changes enrollment intent only. Reconfigure the runtime before retiring tags."
        : "Tag history stays available. Retired terminal tags stop receiving device updates."}
      onClose={busy ? undefined : onCancel}
      width={520}
      bodyClassName="gwFormStack"
      footer={(
        <>
          <button type="button" className="gwGhostBtn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="gwDangerBtn"
            disabled={!valid || busy}
            onClick={() => onConfirm(fr202RetirementConfirmation({ gateway, action, gatewayName, reason }))}
          >
            {busy ? "Working…" : removingProfile ? "Remove profile" : "Retire tags"}
          </button>
        </>
      )}
    >
      <dl className="plcFR202ConfirmTarget">
        <div>
          <dt>Runtime</dt>
          <dd>{gateway?.name}</dd>
        </div>
      </dl>
      {removingProfile ? (
        <div className="gwAlert" role="alert">
          <div className="gwAlertTitle">Scanning and outputs keep running</div>
          <div className="gwAlertBody">Put the equipment in a safe state and stop affected applications first.</div>
        </div>
      ) : null}
      <div className="gwField">
        <label className="gwLabel" htmlFor="fr202-retirement-runtime-name">
          Type <strong>{gateway?.name}</strong> to confirm
        </label>
        <input
          id="fr202-retirement-runtime-name"
          className="gwInput"
          value={gatewayName}
          onChange={(event) => setGatewayName(event.target.value)}
          placeholder={gateway?.name}
          autoComplete="off"
        />
      </div>
      <div className="gwField">
        <label className="gwLabel" htmlFor="fr202-retirement-reason">Reason</label>
        <textarea
          id="fr202-retirement-reason"
          className="gwInput"
          rows={3}
          maxLength={512}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </GatewayModal>
  );
}

export function FR202RetirementMaintenance({ gateway, canConfigure, canWrite, onChanged }) {
  const notify = useNotify();
  const mounted = useRef(true);
  const [status, setStatus] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [dialogAction, setDialogAction] = useState("");
  const [busy, setBusy] = useState(false);
  const durableFR202 = gateway.hardware_profile === FR202_PROFILE_ID;
  // An explicitly removed profile uses "" while its terminal retirement may
  // still be pending. Other named profiles can never have FR202 retirement
  // state, so do not issue a hidden request from every runtime overview.
  const retirementMayExist = durableFR202 || gateway.hardware_profile === "";

  const loadStatus = useCallback(async () => {
    if (!retirementMayExist) return null;
    try {
      const next = await getEdgeFR202RetirementStatus(gateway.id);
      if (!mounted.current) return null;
      setStatus(next);
      setLoadError("");
      return next;
    } catch {
      if (!mounted.current) return null;
      setLoadError("FR202 decommissioning status is temporarily unavailable.");
      return null;
    }
  }, [gateway.id, retirementMayExist]);

  useEffect(() => {
    mounted.current = true;
    loadStatus();
    return () => { mounted.current = false; };
  }, [loadStatus]);

  useEffect(() => {
    const state = status?.latest_command?.status;
    if (state !== "queued" && state !== "sent") return undefined;
    const interval = window.setInterval(loadStatus, 2000);
    return () => window.clearInterval(interval);
  }, [loadStatus, status?.latest_command?.status]);

  const submit = async (confirmation) => {
    const action = dialogAction;
    setBusy(true);
    try {
      if (action === "remove-profile") {
        await removeEdgeFR202HardwareProfile(gateway.id, confirmation);
        setDialogAction("");
        await onChanged?.();
        await loadStatus();
        notify({
          type: "success",
          title: "FR202 profile removed",
          message: "Create and run a new setup command on the device. Terminal tags can be retired after it reconnects without the FR202 profile.",
        });
        return;
      }
      const command = await retireEdgeFR202Tags(gateway.id, confirmation);
      setDialogAction("");
      notify({ type: "success", title: "FR202 retirement queued", message: "Waiting for the device to finish retiring these terminal tags." });
      const outcome = await pollEdgeCommandUntilTerminal({
        gatewayID: gateway.id,
        command,
        isActive: () => mounted.current,
      });
      if (outcome.state === "cancelled") return;
      const refreshed = await loadStatus();
      await onChanged?.();
      if (outcome.state === "succeeded") {
        if (refreshed?.cloud_mirror_state === "retired") {
          notify({ type: "success", title: "FR202 tags retired", message: "Local and cloud terminal definitions/history were preserved as manual tags at Bad quality." });
        } else {
          notify({ type: "warning", title: "Device tags retired", message: "Cleanup is still finishing. It is safe to retry." });
        }
      } else {
        notify({ type: "error", title: "FR202 retirement failed", message: "The device could not finish retiring these tags. Check that it is online and try again." });
      }
    } catch {
      notify({
        type: "error",
        title: action === "remove-profile" ? "Could not remove FR202 profile" : "Could not retire FR202 tags",
        message: "The device could not complete this change. Check that it is online and try again.",
      });
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const relevant = durableFR202 || status?.profile_removal_audited || status?.latest_command;
  if (!relevant) return null;
  const hasRuntimePermission = canConfigure && canWrite;
  const latest = status?.latest_command;
  const result = status?.latest_result;
  const fullyRetired = latest?.status === "succeeded" && status?.cloud_mirror_state === "retired";
  return (
    <details className="plcRuntimeEnrollAdvanced" data-testid="fr202-retirement-maintenance">
      <summary className="gwLabel">
        <img src={chevronIcon} alt="" className="gwIcon gwDisclosureChevron" />
        FR202 decommissioning
      </summary>
      <div className="gwFormStack plcRuntimeEnrollAdvancedBody">
        {loadError ? <div className="gwNotice isWarning"><span>{loadError}</span></div> : null}
        {status?.blocker ? <div className="gwHint">Finish the required device setup before retiring these tags.</div> : null}
        {latest ? (
          <div className="gwEdgeFieldbusRow">
            <div>
              <div className="gwListTitle">Terminal tag retirement</div>
              <div className="gwListMeta">{result ? `${result.retired?.length || 0} retired · ${result.already_retired?.length || 0} already manual · ${result.preserved?.length || 0} unrelated preserved` : "Waiting for the device"}</div>
            </div>
            <GatewayStatusPill tone={fullyRetired ? "isPositive" : latest.status === "failed" ? "isWarning" : "isNeutral"}>
              {fullyRetired ? "Retired" : latest.status === "succeeded" ? "Finishing" : latest.status === "failed" ? "Failed" : latest.status === "sent" ? "Running" : "Queued"}
            </GatewayStatusPill>
          </div>
        ) : null}
        <div className="gwRowActions" style={{ justifyContent: "flex-start" }}>
          {durableFR202 ? (
            <button type="button" className="gwDangerBtn" disabled={!hasRuntimePermission || busy} onClick={() => setDialogAction("remove-profile")}>Remove FR202 profile</button>
          ) : status?.profile_removal_audited ? (
            <button type="button" className="gwDangerBtn" disabled={!hasRuntimePermission || busy || !status?.eligible || latest?.status === "queued" || latest?.status === "sent" || fullyRetired} onClick={() => setDialogAction("retire-tags")}>{latest?.status === "failed" || latest?.status === "succeeded" ? "Retry tag retirement" : "Retire terminal tags"}</button>
          ) : null}
        </div>
        {!hasRuntimePermission ? <div className="gwHint">Configure and Write permissions are both required.</div> : null}
      </div>
      {dialogAction ? (
        <FR202RetirementConfirmModal
          gateway={gateway}
          action={dialogAction}
          busy={busy}
          onCancel={() => setDialogAction("")}
          onConfirm={submit}
        />
      ) : null}
    </details>
  );
}

const fr202StatusLabel = (status) => {
  switch (status) {
    case "available": return "Detected";
    case "detected": return "Detected";
    case "passed": return "Passed";
    case "found": return "Found";
    case "read_write": return "Read/write";
    case "not_enabled": return "Not enabled";
    case "not_detected_optional": return "Not detected";
    case "standard_setup": return "Standard setup";
    case "factory_image": return "Factory image";
    case "needs_review": return "Needs review";
    case "present": return "Present";
    case "recorded": return "Recorded";
    case "invalid": return "Needs review";
    case "attention": return "Needs attention";
    case "not_recorded": return "Standard setup";
    case "calibration_required": return "Calibration required";
    case "self_test": return "Read-only";
    case "actuation_blocked": return "Readback only";
    case "ambiguous": return "Selection required";
    case "blocked": return "Blocked";
    case "incomplete": return "Incomplete";
    case "missing": return "Not detected";
    case "failed": return "Failed";
    default: return "Not reported";
  }
};

const fr202StatusTone = (status) => {
  if (["available", "detected", "passed", "found", "read_write", "self_test", "factory_image"].includes(status)) return "isPositive";
  if (["attention", "calibration_required", "actuation_blocked", "ambiguous", "blocked", "incomplete", "missing", "failed", "invalid", "needs_review"].includes(status)) return "isWarning";
  return "isNeutral";
};

const fr202ComponentFound = (component) => ["available", "detected", "passed", "present", "recorded"]
  .includes(component?.status);

const fr202SafeDetectionRows = (evidence) => [
  {
    id: "digital",
    label: "Digital I/O",
    detail: fr202ComponentFound(evidence.dio) ? "Controller detected." : "Controller not detected.",
    status: fr202ComponentFound(evidence.dio) ? "detected" : "needs_review",
  },
  {
    id: "analog",
    label: "Analog and RTD",
    detail: fr202ComponentFound(evidence.adc)
      ? "Interface detected. Analog setup is available; RTD setup is not enabled."
      : "Interface not detected. Apply the FR202 setup and restart the device.",
    status: fr202ComponentFound(evidence.adc) ? "detected" : "needs_review",
  },
  {
    id: "boot",
    label: "Boot configuration",
    detail: fr202ComponentFound(evidence.vendor_boot_config)
      ? "Required device settings were found."
      : "Required device settings were not found.",
    status: fr202ComponentFound(evidence.vendor_boot_config) ? "found" : "needs_review",
  },
];

const fr202TechnicalRows = (evidence) => [
  {
    id: "analog-hardware",
    label: "Analog hardware",
    status: fr202ComponentFound(evidence.adc) ? "detected" : evidence.adc?.status === "invalid" ? "needs_review" : "not_detected_optional",
  },
  {
    id: "installation",
    label: "Installation",
    status: evidence.factory_image?.status === "recorded"
      ? "factory_image"
      : evidence.factory_image?.status === "invalid" ? "needs_review" : "standard_setup",
  },
];

const fr202TerminalGroupView = (group) => {
  if (group.kind === "analog_input") {
    return { note: "Choose the signal range and calibrate each channel before use.", readiness: "calibration_required" };
  }
  if (group.kind === "rtd_input") {
    return { note: "RTD setup is unavailable until sensor wiring and excitation are qualified.", readiness: "not_enabled" };
  }
  if (group.kind === "digital_input") {
    return { note: "Reads 16 digital inputs. Groups of four share input polarity.", readiness: group.readiness };
  }
  if (group.kind === "digital_output") {
    return { note: "Writes 16 sinking outputs and confirms each state with readback.", readiness: group.readiness };
  }
  return { note: "", readiness: "not_reported" };
};

const FR202_SELF_TEST_FALLBACK_TTL_MS = 2 * 60 * 1000;
const FR202_SELF_TEST_POLL_MS = 1000;

// Follow the command's server-owned expiry instead of an arbitrary UI retry
// count. Temporary status-request failures do not make the action available
// again while the original serial-bus test may still be running.
export async function pollEdgeCommandUntilTerminal({
  gatewayID,
  command,
  listCommands = listEdgeGatewayCommands,
  isActive = () => true,
  wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)),
  now = () => Date.now(),
  onProgress,
}) {
  const startedAt = now();
  const declaredExpiry = Date.parse(command?.expires_at || "");
  const deadline = Number.isFinite(declaredExpiry) && declaredExpiry > startedAt
    ? declaredExpiry
    : startedAt + FR202_SELF_TEST_FALLBACK_TTL_MS;
  let lastCommand = command || null;
  let lastStatusError = null;

  while (isActive()) {
    try {
      const commands = await listCommands(gatewayID);
      const current = Array.isArray(commands)
        ? commands.find((item) => item.id === command?.id)
        : null;
      if (current) lastCommand = current;
      lastStatusError = null;
      if (current?.status === "succeeded" || current?.status === "failed") {
        return { state: current.status, command: current };
      }
      onProgress?.(current?.status === "sent" ? "Running on runtime" : "Waiting for runtime");
    } catch (error) {
      lastStatusError = error;
      onProgress?.("Checking runtime");
    }

    const remaining = deadline - now();
    if (remaining <= 0) {
      return { state: "expired", command: lastCommand, error: lastStatusError };
    }
    await wait(Math.min(FR202_SELF_TEST_POLL_MS, remaining));
  }
  return { state: "cancelled", command: lastCommand };
}

const FR202_ANALOG_SIGNALS = [
  { value: "voltage_0_5", label: "0–5 V", minimum: 0, maximum: 5 },
  { value: "voltage_0_10", label: "0–10 V", minimum: 0, maximum: 10 },
  { value: "voltage_minus5_5", label: "−5–5 V", minimum: -5, maximum: 5 },
  { value: "voltage_minus10_10", label: "−10–10 V", minimum: -10, maximum: 10 },
  { value: "current_0_20ma", label: "0–20 mA", minimum: 0, maximum: 20 },
  { value: "current_4_20ma", label: "4–20 mA", minimum: 4, maximum: 20 },
  { value: "current_4_24ma", label: "4–24 mA", minimum: 4, maximum: 24 },
];
const fr202TagSlug = (value) => String(value || "").trim()
  .replace(/[^A-Za-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 64);

const commandResultObject = (command) => {
  if (command?.result && typeof command.result === "object") return command.result;
  if (typeof command?.result !== "string") return null;
  try { return JSON.parse(command.result); } catch { return null; }
};

function FR202ChannelSetupModal({ gateway, terminal, connector, onClose, onComplete }) {
  const notify = useNotify();
  const mounted = useRef(true);
  const existingMapping = connector?.mappings?.find((mapping) => mapping.native_address === `fr202:${terminal.id}`);
  const fixedExistingTag = existingMapping && !existingMapping.tag_missing ? existingMapping.tag_path : "";
  const [signalType, setSignalType] = useState("");
  const [pollIntervalMs, setPollIntervalMs] = useState(500);
  const [lowReference, setLowReference] = useState(null);
  const [highReference, setHighReference] = useState(null);
  const [lowRaw, setLowRaw] = useState(null);
  const [highRaw, setHighRaw] = useState(null);
  const [tagMode, setTagMode] = useState(fixedExistingTag ? "fixed" : "existing");
  const [tagID, setTagID] = useState("");
  const [tagPath, setTagPath] = useState(fixedExistingTag);
  const [newTagName, setNewTagName] = useState(`${gateway.name}_${terminal.id}`);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmStatus, setConfirmStatus] = useState("");

  useEffect(() => () => { mounted.current = false; }, []);

  const chooseSignal = (value) => {
    const signal = FR202_ANALOG_SIGNALS.find((candidate) => candidate.value === value);
    setSignalType(value);
    setLowReference(signal?.minimum ?? null);
    setHighReference(signal?.maximum ?? null);
    setLowRaw(null);
    setHighRaw(null);
    setError("");
  };

  const capture = async (which) => {
    setBusy(`capture-${which}`);
    setError("");
    try {
      const command = await readEdgeFR202ChannelSample(gateway.id, terminal.id);
      const outcome = await pollEdgeCommandUntilTerminal({
        gatewayID: gateway.id,
        command,
        isActive: () => mounted.current,
      });
      if (outcome.state === "cancelled") return;
      const result = commandResultObject(outcome.command);
      const raw = Number(result?.raw);
      if (outcome.state !== "succeeded" || !Number.isFinite(raw)) {
        throw new Error("capture failed");
      }
      if (which === "low") setLowRaw(raw);
      else setHighRaw(raw);
    } catch {
      setError(`Could not read ${terminal.id}. Check the wiring and try again.`);
    } finally {
      if (mounted.current) setBusy("");
    }
  };

  const prepareTagForReview = async () => {
    if (tagMode === "fixed") return { tagPath: fixedExistingTag, pendingTag: null };
    if (tagMode === "existing") {
      if (!tagPath) throw new Error("Choose a number tag.");
      const evidence = await inspectEdgeTagCatalog(gateway.id, [tagPath]);
      const eligibility = edgeConnectorTagEligibility(evidence, tagPath, "input");
      if (!eligibility.eligible || eligibility.tagType !== "number") {
        throw new Error(eligibility.eligible ? "Choose a number tag." : eligibility.message);
      }
      return { tagPath, pendingTag: null };
    }
    const slug = fr202TagSlug(newTagName);
    if (!slug) throw new Error("Name the new tag.");
    const fullPath = `default/${slug}`;
    return {
      tagPath: fullPath,
      pendingTag: {
        fullPath,
        path: slug,
        name: newTagName.trim() || slug,
        type: "float64",
        writable: false,
        direction: "input",
        description: `${terminal.id} on ${gateway.name}`,
        created: false,
      },
    };
  };

  const finish = async () => {
    setBusy("preparing");
    setError("");
    try {
      const plannedTagPath = tagMode === "fixed"
        ? fixedExistingTag
        : tagMode === "existing" ? tagPath : `default/${fr202TagSlug(newTagName)}`;
      const spec = buildFR202AnalogConnectorSpec({
        connectorID: connector?.connector_id || "",
        desiredState: connector ? connector.desired_state : "running",
        terminal: terminal.id, signalType,
        pollIntervalMs, tagPath: plannedTagPath,
        low: { raw: lowRaw, reference: lowReference },
        high: { raw: highRaw, reference: highReference },
      });
      const prepared = await prepareTagForReview();
      if (prepared.tagPath !== plannedTagPath) throw new Error("The selected tag changed. Choose it again.");
      const preview = prepared.pendingTag
        ? spec
        : await previewEdgeConnector(gateway.id, spec, connector?.desired_revision);
      // Setting up a terminal is one step. The preview call stays as the
      // non-mutating validation gate and reports failures inline on this form,
      // where the calibration and tag can actually be corrected.
      await confirm({ preview, spec, pendingTag: prepared.pendingTag });
    } catch (setupError) {
      const raw = String(setupError?.message || "");
      const message = setupError?.publicMessage || (/already exists|conflict/i.test(raw)
        ? "A tag with this name already exists. Choose it under Existing tag, or use another name."
        : "The channel could not be prepared. Check the calibration and tag, then try again.");
      setError(message);
    } finally {
      if (mounted.current) setBusy("");
    }
  };

  const confirm = async (target) => {
    if (!target?.preview) return;
    setBusy("confirm");
    setConfirmStatus("Applying…");
    setError("");
    try {
      let confirmedPreview = target.preview;
      if (target.pendingTag) {
        confirmedPreview = await preparePendingConnector({
          gateway,
          pendingTag: target.pendingTag,
          spec: target.spec,
          expectedRevision: connector?.desired_revision,
          onStatus: setConfirmStatus,
        });
      }
      setConfirmStatus(`Sending to ${gateway.name}…`);
      await confirmEdgeConnector(gateway.id, confirmedPreview);
      notify({
        type: "success", title: `${terminal.id} setup sent`,
        message: target.spec.desired_state === "stopped"
          ? `${terminal.id} was updated and will remain off.`
          : `${gateway.name} will begin reporting the calibrated value when it confirms the change.`,
      });
      await onComplete?.();
    } catch (setupError) {
      const raw = String(setupError?.message || "");
      setError(setupError?.publicMessage || (/already exists|conflict/i.test(raw)
        ? "A tag with this name already exists. Cancel and choose it under Existing tag, or use another name."
        : /offline|not online|unreachable|not connected/i.test(raw)
          ? `${gateway.name} is offline. Reconnect it, then try Connect again.`
          : "The channel could not be configured. Check the setup and try Connect again."));
    } finally {
      if (mounted.current) {
        setConfirmStatus("");
        setBusy("");
      }
    }
  };

  const signal = FR202_ANALOG_SIGNALS.find((candidate) => candidate.value === signalType);
  const tagReady = tagMode === "fixed"
    ? Boolean(fixedExistingTag)
    : tagMode === "new" ? Boolean(fr202TagSlug(newTagName)) : Boolean(tagPath);
  const ready = signalType && Number.isFinite(lowRaw) && Number.isFinite(highRaw)
    && Number.isFinite(Number(lowReference)) && Number.isFinite(Number(highReference))
    && tagReady;

  return (
    <GatewayModal
      title={`Set up ${terminal.id}`}
      subtitle="Connect a known signal, capture two points, then choose its tag."
      onClose={busy ? undefined : onClose}
      width={620}
      bodyClassName="gwFormStack"
      footer={(
        <>
          <button type="button" className="gwGhostBtn" onClick={onClose} disabled={Boolean(busy)}>Cancel</button>
          <button type="button" className="gwPrimaryBtn" onClick={finish} disabled={!ready || Boolean(busy)}>
            {busy ? (connector ? "Saving…" : "Connecting…") : connector ? "Save changes" : "Connect"}
          </button>
        </>
      )}
    >
      {error ? <div className="gwAlert" role="alert"><div className="gwAlertTitle">Check the setup</div><div className="gwAlertBody">{error}</div></div> : null}
      {busy === "confirm" && confirmStatus ? <div className="gwHint" role="status">{confirmStatus}</div> : null}
      <div className="gwFormStack">
        <div className="gwField">
          <label className="gwLabel">Signal type</label>
          <Select value={signalType} onChange={chooseSignal} options={FR202_ANALOG_SIGNALS} placeholder="Choose the connected signal…" disabled={Boolean(busy)} />
        </div>
        {signal ? (
          <>
            <div className="gwConnectionFormGroup">
              <div className="gwLabel">First calibration point</div>
              <div className="gwConnectionFields">
                <div className="gwField">
                  <label className="gwLabel">Known value ({signal.label.includes("mA") ? "mA" : "V"})</label>
                  <NudgeNumber value={lowReference} onChange={setLowReference} min={signal.minimum} max={signal.maximum} allowDecimal aria-label="First known calibration value" disabled={Boolean(busy)} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Device reading</label>
                  <button type="button" className="gwSecondaryBtn" onClick={() => capture("low")} disabled={Boolean(busy)}>
                    {busy === "capture-low" ? "Reading…" : Number.isFinite(lowRaw) ? "Capture again" : "Capture"}
                  </button>
                  {Number.isFinite(lowRaw) ? <small className="gwHint">Captured</small> : null}
                </div>
              </div>
            </div>
            <div className="gwConnectionFormGroup">
              <div className="gwLabel">Second calibration point</div>
              <div className="gwConnectionFields">
                <div className="gwField">
                  <label className="gwLabel">Known value ({signal.label.includes("mA") ? "mA" : "V"})</label>
                  <NudgeNumber value={highReference} onChange={setHighReference} min={signal.minimum} max={signal.maximum} allowDecimal aria-label="Second known calibration value" disabled={Boolean(busy)} />
                </div>
                <div className="gwField">
                  <label className="gwLabel">Device reading</label>
                  <button type="button" className="gwSecondaryBtn" onClick={() => capture("high")} disabled={Boolean(busy)}>
                    {busy === "capture-high" ? "Reading…" : Number.isFinite(highRaw) ? "Capture again" : "Capture"}
                  </button>
                  {Number.isFinite(highRaw) ? <small className="gwHint">Captured</small> : null}
                </div>
              </div>
            </div>
          </>
        ) : null}
        <div className="gwConnectionFormGroup">
          <div className="gwLabel">Tag</div>
          {tagMode === "fixed" ? <code className="gwEdgeIOTagPath">{fixedExistingTag}</code> : (
            <>
              <div className="gwRowActions" style={{ justifyContent: "flex-start" }}>
                <button type="button" className={tagMode === "existing" ? "gwSecondaryBtn" : "gwGhostBtn"} onClick={() => setTagMode("existing")} disabled={Boolean(busy)}>Existing tag</button>
                <button type="button" className={tagMode === "new" ? "gwSecondaryBtn" : "gwGhostBtn"} onClick={() => setTagMode("new")} disabled={Boolean(busy)}>New tag</button>
              </div>
              {tagMode === "existing" ? (
                <TagPicker value={tagID} displayValue={tagPath} placeholder="Choose a number tag…" disabled={Boolean(busy)} onChange={(id, path) => { setTagID(id); setTagPath(path || ""); setError(""); }} />
              ) : (
                <div className="gwField">
                  <input className="gwInput" value={newTagName} onChange={(event) => setNewTagName(event.target.value)} placeholder="Tag name" disabled={Boolean(busy)} />
                  <small className="gwHint">Creates a read-only number tag in Tags.</small>
                </div>
              )}
            </>
          )}
        </div>
        <details className="plcRuntimeEnrollAdvanced">
          <summary className="gwLabel"><img src={chevronIcon} alt="" className="gwIcon gwDisclosureChevron" />Sampling</summary>
          <div className="gwField plcRuntimeEnrollAdvancedBody">
            <label className="gwLabel">Update every (ms)</label>
            <NudgeNumber value={pollIntervalMs} onChange={setPollIntervalMs} min={100} max={60000} step={100} aria-label="Analog polling interval" disabled={Boolean(busy)} />
          </div>
        </details>
      </div>
    </GatewayModal>
  );
}

function GenericHardwarePage({ gateway, hardware, loading, onClose, onConnections, onRefresh }) {
  const rows = [...hardware.groups, ...hardware.resources];
  return (
    <div className="gwConnectionsPage">
      <div className="gwConnectionsPageInner">
        <div className="gwPageHeader gwConnectionsPageHeader">
          <div>
            <button type="button" className="gwGhostBtn plcRuntimeDetailBack" onClick={onClose}>
              <img src={chevronIcon} alt="" className="gwIcon" />
              {gateway.name}
            </button>
            <div className="gwPageTitle">Hardware &amp; I/O</div>
            <div className="gwPageSubtitle">Review local hardware detected on this runtime. Nothing here changes an output.</div>
          </div>
          <button type="button" className="gwGhostBtn" onClick={onRefresh} disabled={loading} aria-busy={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div className="gwConnectionsPageBody gwFormStack">
          <section className="gwCardSection" aria-labelledby="runtime-hardware-status-title">
            <div className="gwSectionTitle" id="runtime-hardware-status-title">Hardware status</div>
            <div className="gwCardSectionBody gwFormStack">
              <div className="gwEdgeFieldbusRow">
                <div>
                  <div className="gwListTitle">{hardware.profile?.label || "Local hardware"}</div>
                  <div className="gwListMeta">{hardware.summary}</div>
                </div>
                <GatewayStatusPill tone={hardware.tone}>{hardware.statusLabel}</GatewayStatusPill>
              </div>
              {hardware.stale ? <div className="gwHint">Showing the last hardware report. Reconnect this runtime to refresh it.</div> : null}
              {hardware.truncated ? <div className="gwHint">Some interfaces are not shown because this runtime reported more hardware than the inventory limit. Disconnect unused devices or contact support if the interface you need is missing.</div> : null}
            </div>
          </section>

          {rows.length > 0 ? (
            <section className="gwCardSection" aria-labelledby="runtime-detected-interfaces-title">
              <div className="gwSectionTitle" id="runtime-detected-interfaces-title">Detected interfaces</div>
              <div className="gwCardSectionBody">
                {rows.map((row) => {
                  // The page-level banner already communicates that the report
                  // is cached. Keep each interface's actionable status visible.
                  const view = runtimeHardwareView({ status: row.status }, { ...gateway, status: "online" });
                  const pointLabel = row.count == null
                    ? ""
                    : ` · ${row.count} ${row.count === 1 ? "point" : "points"}`;
                  return (
                    <div className="gwEdgeFieldbusRow" key={row.key}>
                      <div>
                        <div className="gwListTitle">{row.label}{pointLabel}</div>
                        <div className="gwListMeta">{row.detail || view.next}</div>
                      </div>
                      <GatewayStatusPill tone={view.tone}>{view.statusLabel}</GatewayStatusPill>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : (
            <div className="gwEmptyState gwEmptyCompact">
              <div className="gwEmptyTitle">{hardware.status === "unknown" ? "Waiting for hardware detection" : "No local I/O detected"}</div>
              <div className="gwEmptyBody">{hardware.next}</div>
            </div>
          )}

          <div className="gwRowActions" style={{ justifyContent: "flex-start" }}>
            <button type="button" className="gwPrimaryBtn" onClick={onConnections}>Manage connections</button>
            <button type="button" className="gwGhostBtn" onClick={onClose}>Back to {gateway.name}</button>
          </div>
          <div className="gwHint">Detected hardware is never assigned an electrical mode or enabled as an output until you review its connection.</div>
        </div>
      </div>
    </div>
  );
}

function HardwareLoadStatePage({ gateway, failed, onClose, onRetry }) {
  return (
    <div className="gwConnectionsPage">
      <div className="gwConnectionsPageInner">
        <div className="gwPageHeader gwConnectionsPageHeader">
          <div>
            <button type="button" className="gwGhostBtn plcRuntimeDetailBack" onClick={onClose}>
              <img src={chevronIcon} alt="" className="gwIcon" />
              {gateway.name}
            </button>
            <div className="gwPageTitle">Hardware &amp; I/O</div>
            <div className="gwPageSubtitle">Review local hardware detected on this runtime.</div>
          </div>
        </div>
        <div className="gwConnectionsPageBody">
          {failed ? (
            <div className="gwEmptyState gwEmptyCompact">
              <div className="gwEmptyTitle">Could not load hardware</div>
              <div className="gwEmptyBody">Check the connection and try again.</div>
              <div className="gwEmptyActions">
                <button type="button" className="gwPrimaryBtn" onClick={onRetry}>Retry</button>
                <button type="button" className="gwGhostBtn" onClick={onClose}>Back to {gateway.name}</button>
              </div>
            </div>
          ) : <div className="gwHint">Loading hardware…</div>}
        </div>
      </div>
    </div>
  );
}

// Generic entry point. New control planes provide a bounded hardware view;
// older ones fall back to the existing authenticated runtime evidence. The
// full FR202 commissioning workflow remains the profile-specific renderer.
export function HardwareIOPage({ gateway, canConfigure, onClose, onConnections, onChanged }) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [legacyFallback, setLegacyFallback] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const mounted = useRef(true);
  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const next = await getEdgeRuntimeHardware(gateway.id);
      if (mounted.current) {
        setPayload(next);
        setLegacyFallback(false);
      }
    } catch (error) {
      if (mounted.current) {
        setPayload(null);
        setLegacyFallback(error?.status === 404);
        setLoadFailed(error?.status !== 404);
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [gateway.id]);
  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  const hardware = runtimeHardwareView(payload, gateway);
  const runtime = normalizeRuntime(gateway.runtime);
  const fr202 = hardware.profile?.value === FR202_PROFILE_ID
    || gateway.hardware_profile === FR202_PROFILE_ID
    || Boolean(runtime?.fr202_io);
  const usefulLegacyEvidence = fr202 || hardware.groups.length > 0 || hardware.resources.length > 0;
  if (loadFailed) {
    return <HardwareLoadStatePage gateway={gateway} failed onClose={onClose} onRetry={load} />;
  }
  if ((!loaded || loading) && payload === null && !legacyFallback && !usefulLegacyEvidence) {
    return <HardwareLoadStatePage gateway={gateway} onClose={onClose} onRetry={load} />;
  }
  if (fr202) {
    return (
      <FR202HardwareDetailPage
        gateway={gateway}
        canConfigure={canConfigure}
        hardwareView={hardware}
        onClose={onClose}
        onChanged={onChanged}
      />
    );
  }
  return (
    <GenericHardwarePage
      gateway={gateway}
      hardware={hardware}
      loading={loading}
      onClose={onClose}
      onConnections={onConnections || onClose}
      onRefresh={load}
    />
  );
}

// Compatibility export for callers and bookmarks from the original
// FR202-only page. New code should use HardwareIOPage.
export const FR202HardwarePage = HardwareIOPage;

// Full page, not a modal. The terminal map alone is 38 rows, which a dialog
// can only show by scrolling a box inside a box — and it is reference material
// an operator reads while wiring, so it needs a URL they can return to. Routed
// as ?runtime=<id>&view=hardware. The former view=fr202 route remains an alias,
// so browser bookmarks continue to work.
function FR202HardwareDetailPage({ gateway, canConfigure, onClose, onChanged, hardwareView }) {
  const notify = useNotify();
  const mounted = useRef(true);
  const [activeStep, setActiveStep] = useState("detect");
  const [running, setRunning] = useState(false);
  const [commandState, setCommandState] = useState("");
  const [terminalDetailsOpen, setTerminalDetailsOpen] = useState(false);
  const [analogConnectors, setAnalogConnectors] = useState([]);
  const [terminalTags, setTerminalTags] = useState([]);
  const [setupTerminal, setSetupTerminal] = useState(null);
  const runtime = normalizeRuntime(gateway.runtime);
  const io = runtime?.fr202_io;
  const evidence = io?.evidence || {};
  const report = io?.last_self_test || null;
  const groups = Array.isArray(io?.terminal_groups) ? io.terminal_groups : [];
  const terminals = Array.isArray(io?.terminals) ? io.terminals : [];

  const loadAnalogConnectors = useCallback(async () => {
    try {
      const inventory = await listEdgeConnectors(gateway.id);
      if (!mounted.current) return;
      setAnalogConnectors((Array.isArray(inventory?.connectors) ? inventory.connectors : [])
        .filter((connector) => connector.kind === "fr202_analog"));
      setTerminalTags(Array.isArray(inventory?.fr202_terminals) ? inventory.fr202_terminals : []);
    } catch {
      // The hardware map still works when connector status is temporarily
      // unavailable; setup itself returns a clear error if attempted.
    }
  }, [gateway.id]);

  useEffect(() => {
    mounted.current = true;
    loadAnalogConnectors();
    return () => { mounted.current = false; };
  }, [loadAnalogConnectors]);

  const runSelfTest = async () => {
    setRunning(true);
    setCommandState("Queued");
    try {
      const command = await runEdgeFR202SelfTest(gateway.id);
      const outcome = await pollEdgeCommandUntilTerminal({
        gatewayID: gateway.id,
        command,
        isActive: () => mounted.current,
        onProgress: setCommandState,
      });
      if (outcome.state === "cancelled") return;
      await onChanged?.();
      if (outcome.state === "succeeded") {
        setCommandState("Complete");
        notify({
          type: "success",
          title: "FR202 hardware test finished",
          message: "The device completed its built-in I/O checks without changing any outputs.",
        });
        return;
      }
      if (outcome.state === "failed") {
        setCommandState("Failed");
        notify({
          type: "error",
          title: "FR202 hardware test failed",
          message: "The runtime could not complete the built-in I/O test. Check the device connection and try again.",
        });
        return;
      }
      setCommandState("Expired");
      notify({
        type: "error",
        title: "FR202 hardware test expired",
        message: "The runtime did not finish the test. Check that it is online and try again.",
      });
    } catch {
      setCommandState("");
      notify({ type: "error", title: "Could not run FR202 hardware test", message: "Check that the runtime is online and try again." });
    } finally {
      if (mounted.current) setRunning(false);
    }
  };

  const detectionRows = fr202SafeDetectionRows(evidence);
  const technicalRows = fr202TechnicalRows(evidence);
  const digitalInventory = Array.isArray(report?.checks)
    ? report.checks.find((check) => check.id === "dio_inventory")
    : null;
  const digitalTestPassed = report?.status === "passed" || digitalInventory?.status === "passed";
  const inputCount = Number(report?.dio?.inputs);
  const outputCount = Number(report?.dio?.outputs);
  const digitalTestDetail = Number.isFinite(inputCount) && Number.isFinite(outputCount) && inputCount > 0 && outputCount > 0
    ? `Read ${inputCount} inputs and ${outputCount} output states without changing any outputs.`
    : "Read digital inputs and output states without changing any outputs.";
  const steps = [
    { id: "detect", label: "Detect" },
    { id: "map", label: "Terminal map" },
    { id: "test", label: "Test" },
  ];
  const activeStepIndex = steps.findIndex((step) => step.id === activeStep);

  return (
    <div className="gwConnectionsPage">
      <div className="gwConnectionsPageInner">
        <div className="gwPageHeader gwConnectionsPageHeader">
          <div>
            <button type="button" className="gwGhostBtn plcRuntimeDetailBack" onClick={onClose}>
              <img src={chevronIcon} alt="" className="gwIcon" />
              {gateway.name}
            </button>
            <div className="gwPageTitle" id="fr202-setup-title">Hardware &amp; I/O</div>
            <div className="gwPageSubtitle">{hardwareView?.profile?.label || "OnLogic Factor 202"} · Review built-in I/O and test it without changing outputs.</div>
          </div>
        </div>
        <div className="gwConnectionsPageBody gwFormStack">
          <GatewayTabs
        items={steps}
        active={activeStep}
        onChange={setActiveStep}
        ariaLabel="Hardware setup steps"
        idPrefix="fr202-setup-tab"
        panelIdPrefix="fr202-setup"
      />
      <section
        className="gwEdgeProgramSection plcRuntimeFR202Step"
        id="fr202-setup-panel-detect"
        role="tabpanel"
        aria-labelledby="fr202-setup-tab-detect"
        hidden={activeStep !== "detect"}
      >
        <div className="gwSectionTitle" id="fr202-detection-title">Hardware status</div>
        {!io ? <div className="gwNotice isWarning"><div><strong>Runtime update required</strong></div><div>Update this runtime to check its built-in I/O.</div></div> : (
          <>
            {detectionRows.map((row) => (
              <div className="gwEdgeFieldbusRow" key={row.id}>
                <div><div className="gwListTitle">{row.label}</div><div className="gwListMeta">{row.detail}</div></div>
                <GatewayStatusPill tone={fr202StatusTone(row.status)}>{fr202StatusLabel(row.status)}</GatewayStatusPill>
              </div>
            ))}
            <details className="plcRuntimeEnrollAdvanced">
              <summary className="gwLabel">
                <img src={chevronIcon} alt="" className="gwIcon gwDisclosureChevron" />
                Technical details
              </summary>
              <div className="gwFormStack plcRuntimeEnrollAdvancedBody">
                {technicalRows.map((row) => (
                  <div className="gwEdgeFieldbusRow" key={row.id}>
                    <div className="gwListTitle">{row.label}</div>
                    <GatewayStatusPill tone={fr202StatusTone(row.status)}>{fr202StatusLabel(row.status)}</GatewayStatusPill>
                  </div>
                ))}
              </div>
            </details>
          </>
        )}
      </section>

      <section
        className="gwEdgeProgramSection plcRuntimeFR202Step"
        id="fr202-setup-panel-map"
        role="tabpanel"
        aria-labelledby="fr202-setup-tab-map"
        hidden={activeStep !== "map"}
      >
        <div className="gwSectionTitle" id="fr202-map-title">Terminal map</div>
        {groups.length === 0 ? <div className="gwHint">Connect this runtime to load its terminal map.</div> : groups.map((group) => {
          const view = fr202TerminalGroupView(group);
          return (
            <div className="gwEdgeFieldbusRow" key={group.kind}>
              <div>
                <div className="gwListTitle">{group.label} · {group.range}</div>
                <div className="gwListMeta">{view.note}</div>
              </div>
              <div className="gwRowActions">
                <GatewayStatusPill tone={fr202StatusTone(view.readiness)}>{fr202StatusLabel(view.readiness)}</GatewayStatusPill>
                {group.kind === "analog_input" ? (
                  <button type="button" className="gwRowAction" onClick={() => setTerminalDetailsOpen(true)}>Set up channels</button>
                ) : null}
              </div>
            </div>
          );
        })}
        {terminals.length > 0 ? (
          <div className="plcRuntimeFR202TerminalMap">
            <button
              type="button"
              className="gwGhostBtn plcRuntimeFR202TerminalToggle"
              aria-expanded={terminalDetailsOpen}
              aria-controls="fr202-terminal-assignments"
              onClick={() => setTerminalDetailsOpen((open) => !open)}
            >
              <img src={chevronIcon} alt="" aria-hidden="true" />
              <span>{terminalDetailsOpen ? "Hide terminal assignments" : `Review ${terminals.length} terminal assignments`}</span>
            </button>
            <div id="fr202-terminal-assignments" hidden={!terminalDetailsOpen} className="plcRuntimeFR202TerminalList">
              {terminals.map((terminal) => {
                const folder = terminal.kind === "digital_input"
                  ? "Digital Inputs"
                  : terminal.kind === "digital_output" ? "Digital Outputs" : "";
                // The cloud inventory owns tag identity. Never reconstruct a
                // default path here: users may move or rename ordinary tags.
                const reportedTerminal = terminalTags.find((item) => (
                  String(item.terminal || item.id || "").toUpperCase() === String(terminal.id || "").toUpperCase()
                ));
                const tagPath = folder ? String(reportedTerminal?.tag_path || terminal.tag_path || "") : "";
                const actuationBlocked = terminal.readiness === "actuation_blocked";
                const calibrationRequired = terminal.kind === "analog_input";
                const configuredConnector = calibrationRequired
                  ? analogConnectors.find((connector) => Array.isArray(connector.mappings)
                    && connector.mappings.some((mapping) => mapping.native_address === `fr202:${terminal.id}`))
                  : null;
                const configuredMapping = configuredConnector?.mappings?.find((mapping) => mapping.native_address === `fr202:${terminal.id}`);
                const terminalDetail = terminal.kind === "rtd_input"
                  ? "RTD setup is not enabled in this release."
                  : calibrationRequired
                    ? "Complete channel setup and calibration before use."
                    : terminal.kind === "digital_output" ? "Digital output" : "Digital input";
                return (
                  <div className="gwEdgeFieldbusRow" key={terminal.id}>
                    <div>
                      <div className="gwListTitle">{terminal.label}</div>
                      <div className="gwListMeta">{terminalDetail}</div>
                    </div>
                    {folder ? (
                      <div className="gwRowActions">
                        <code className="gwEdgeIOTagPath">
                          {tagPath || (reportedTerminal?.tag_missing ? "Tag missing" : "Waiting for tag")}
                        </code>
                        {terminal.kind === "digital_output" ? (
                          <GatewayStatusPill tone={actuationBlocked ? "isWarning" : "isPositive"}>
                            {actuationBlocked ? "Readback only" : "Read/write"}
                          </GatewayStatusPill>
                        ) : null}
                      </div>
                    ) : terminal.kind === "rtd_input" ? (
                      <GatewayStatusPill tone="isNeutral">Not enabled</GatewayStatusPill>
                    ) : configuredConnector ? (
                      <div className="gwRowActions">
                        <code className="gwEdgeIOTagPath">{configuredMapping?.tag_missing ? "Tag missing" : configuredMapping?.tag_path}</code>
                        <button
                          type="button"
                          className="gwRowAction"
                          disabled={!canConfigure || gateway.status !== "online"}
                          onClick={() => setSetupTerminal({ terminal, connector: configuredConnector })}
                        >
                          {configuredMapping?.tag_missing ? "Repair setup" : "Recalibrate"}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="gwSecondaryBtn"
                        disabled={!canConfigure || gateway.status !== "online" || !fr202ComponentFound(evidence.adc)}
                        onClick={() => setSetupTerminal({ terminal, connector: null })}
                      >
                        Set up
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>

      <section
        className="gwEdgeProgramSection plcRuntimeFR202Step"
        id="fr202-setup-panel-test"
        role="tabpanel"
        aria-labelledby="fr202-setup-tab-test"
        hidden={activeStep !== "test"}
      >
        <div className="gwSectionTitle" id="fr202-test-title">Built-in I/O test</div>
        <div className="gwHint">Reads digital states and every analog converter input without changing any outputs.</div>
        {report ? (
          <div className="gwFormStack plcRuntimeFR202TestResult">
            <div className="gwEdgeFieldbusRow">
              <div>
                <div className="gwListTitle">{digitalTestPassed ? "Digital I/O test passed" : "Digital I/O test needs attention"}</div>
                <div className="gwListMeta">{digitalTestPassed ? digitalTestDetail : "Check the device connection and try again."}</div>
              </div>
              <GatewayStatusPill tone={digitalTestPassed ? "isPositive" : "isWarning"}>{digitalTestPassed ? "Passed" : "Needs attention"}</GatewayStatusPill>
            </div>
            {report?.adc?.read_pairs === 8 ? (
              <div className="gwHint">Analog hardware passed. Calibrate a channel before using its measurements.</div>
            ) : (
              <div className="gwHint">Analog hardware was not verified. Check the device setup and try again.</div>
            )}
          </div>
        ) : <div className="gwHint">No hardware test has been run on this runtime.</div>}
        <div className="gwRowActions" style={{ justifyContent: "flex-start" }}>
          <button type="button" className="gwPrimaryBtn" onClick={runSelfTest} disabled={!canConfigure || gateway.status !== "online" || !io || running}>
            {running ? commandState || "Running…" : "Run hardware test"}
          </button>
          {gateway.status !== "online" ? <span className="gwHint">The runtime must be online.</span> : null}
        </div>
      </section>

          {/* Step nav lives in the body, not a sticky footer: on a page the
              tabs above are the primary navigation, and these are just a
              linear path through them. The last step returns to the runtime
              rather than saying "Done" — nothing here is unsaved, so there is
              no completion to acknowledge. */}
          <div className="gwRowActions plcRuntimeFR202StepNav">
            {activeStepIndex > 0 && activeStepIndex < steps.length - 1 ? (
              <button
                type="button"
                className="gwGhostBtn"
                onClick={() => setActiveStep(steps[activeStepIndex - 1].id)}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              className="gwPrimaryBtn"
              onClick={() => {
                if (activeStepIndex >= steps.length - 1) onClose();
                else setActiveStep(steps[activeStepIndex + 1].id);
              }}
            >
              {activeStepIndex >= steps.length - 1 ? `Back to ${gateway.name}` : "Next"}
            </button>
          </div>
          {setupTerminal ? (
            <FR202ChannelSetupModal
              gateway={gateway}
              terminal={setupTerminal.terminal}
              connector={setupTerminal.connector}
              onClose={() => setSetupTerminal(null)}
              onComplete={async () => {
                setSetupTerminal(null);
                await Promise.all([loadAnalogConnectors(), Promise.resolve(onChanged?.())]);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
